import React, { ChangeEvent, useState, MouseEvent, useEffect } from "react";
import { AnticipationModal, ModalResponse } from "../../types";
import {
  ConfirmationModal,
  ConfirmButton,
  GhostButton,
  ModalActions,
  ModalCard,
  ModalGrid,
  ModalHeader,
  ModalOverlay,
  ModalSub,
  OverlayStyle,
  Resumo,
} from "../../../../styles";
import { T2 } from "../../../../../../styles/titles";
import { formatMoney } from "../../../../../../utils/generalFormat";
import { formatCurrency } from "../../../../../../utils/formatViews";
import { InputWrapper } from "../../../../anticipationFees/anticipationFeeMenu/styles";
import { parsePtBrCurrencyToNumber } from "../../../utils";
import { toast } from "react-toastify";
import { Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";

export const Modal: React.FC<AnticipationModal> = (
  props: AnticipationModal
) => {
  const { isShowing, data, totalLiquidValue, onConfirmation } = props;
  const [anticipationFee, setAnticipationFee] = useState<string>("");
  const [otherBillingsFee, setOtherBillingsFee] = useState<string>("");
  const [rebuyFee, setRebuyFee] = useState<string>("");
  const [newReceiveDate, setNewReceiveDate] = useState<string>("");
  const [dateInputType, setDateInputType] = useState<"text" | "date">("text");
  const todayStr = new Date().toISOString().slice(0, 10);
  const [exemptFees, setExemptFees] = useState<boolean>(false);

  useEffect(() => {
    if (!isShowing) {
      setAnticipationFee("");
      setNewReceiveDate("");
      setRebuyFee("");
      setOtherBillingsFee("");
    }
  }, [isShowing]);

  const handleAnticipationFee = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/\D/g, "");

    if (rawValue === "") {
      setAnticipationFee("");
      return;
    }
    const trimmedValue = rawValue.replace(/^0+/, "") || "0";

    let formattedValue = "";

    if (trimmedValue.length === 1) {
      formattedValue = `0.0${trimmedValue}`;
    } else if (trimmedValue.length === 2) {
      formattedValue = `0.${trimmedValue}`;
    } else {
      const integerPart = trimmedValue.slice(0, -2);
      const decimalPart = trimmedValue.slice(-2);
      formattedValue = `${integerPart}.${decimalPart}`;
    }

    setAnticipationFee(formattedValue);
  };

  const handleOtherBillingsFee = (e: any) => {
    let rawValue = e.target.value.replace(/\D/g, "");
    const formattedValue = formatCurrency(rawValue);

    setOtherBillingsFee(formattedValue);
  };

  const handleRebuyFee = (e: any) => {
    let rawValue = e.target.value.replace(/\D/g, "");
    const formattedValue = formatCurrency(rawValue);

    setRebuyFee(formattedValue);
  };

  const handleNewReceiveDate = (event: ChangeEvent<HTMLInputElement>) => {
    setNewReceiveDate(event.target.value);
  };

  const confirmationValidator = () => {
    const feeNumber = Number(anticipationFee);
    const isValidFee =
      Number.isFinite(feeNumber) && feeNumber > 0 && feeNumber < 100;
    const isDateFilled = Boolean(newReceiveDate?.trim());
    const isDateTodayOrFuture = isDateFilled && newReceiveDate >= todayStr;

    const isValid = isValidFee && isDateTodayOrFuture;

    if (!isValidFee) toast.error("Taxa de antecipação inválida!!");
    else if (!isDateTodayOrFuture)
      toast.error("Nova data de recebimento inválida!!");

    return isValid;
  };

  const handleConfirmation = (event: MouseEvent<HTMLButtonElement>) => {
    const isConfirmed = event.currentTarget.value === "1";

    if (isConfirmed && onConfirmation) {
      const isValid = confirmationValidator();
      if (isValid) {
        const anticipationData: ModalResponse = {
          confirmation: true,
          anticipationFee: parseFloat(anticipationFee),
          newReceiveDate: newReceiveDate,
          rebuyFee: parsePtBrCurrencyToNumber(rebuyFee),
          otherBillingsFee: parsePtBrCurrencyToNumber(otherBillingsFee),
          exemptFee: exemptFees,
        };

        onConfirmation(anticipationData);
      }
    } else if (onConfirmation) {
      onConfirmation({ confirmation: false });
    }
  };

  const changeExemptFeesAntd = (e: CheckboxChangeEvent) => {
    setExemptFees(e.target.checked);
  };

  return (
    <>
      {isShowing && (
        <>
          <ModalOverlay
            onClick={() => onConfirmation?.({ confirmation: false })}
          />
          <ModalCard
            role="dialog"
            aria-modal="true"
            aria-labelledby="simulacao-title"
            onClick={(e) => e.stopPropagation()} // impede fechar ao clicar dentro
          >
            <ModalHeader>
              <T2 id="simulacao-title">Simulação</T2>
              <ModalSub>Preencha os campos</ModalSub>
            </ModalHeader>

            <ModalGrid>
              <InputWrapper>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Taxa de antecipação pontual"
                  value={anticipationFee}
                  onChange={handleAnticipationFee}
                />
                {anticipationFee && <span className="suffix">%</span>}
              </InputWrapper>

              <InputWrapper className="currency">
                <span className="prefix">R$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Taxa de recompra"
                  value={rebuyFee}
                  onChange={handleRebuyFee}
                />
              </InputWrapper>

              <InputWrapper className="currency">
                <span className="prefix">R$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Outras taxas"
                  value={otherBillingsFee}
                  onChange={handleOtherBillingsFee}
                />
              </InputWrapper>

              <InputWrapper>
                <input
                  type={dateInputType}
                  placeholder="Data de antecipação pontual"
                  value={newReceiveDate}
                  onChange={handleNewReceiveDate}
                  onFocus={() => setDateInputType("date")}
                  required
                />
              </InputWrapper>
            </ModalGrid>

            <Checkbox checked={exemptFees} onChange={changeExemptFeesAntd}>
              Isentar Taxas
            </Checkbox>
            <Resumo>
              Deseja realmente simular a antecipação de {data} recebível(eis)
              com o valor de {formatMoney(totalLiquidValue)}?
            </Resumo>

            <ModalActions>
              <GhostButton onClick={handleConfirmation} value="0">
                Cancelar
              </GhostButton>
              <ConfirmButton onClick={handleConfirmation} value="1">
                Confirmar
              </ConfirmButton>
            </ModalActions>
          </ModalCard>
        </>
      )}
    </>
  );
};
